import { useCallback, useMemo, useRef, useState } from "react";

import { showFile } from "../questionnaire/fileActions";
import type { Prefill } from "../questionnaire/prefill";

import { deleteSupportingDocument, suggestAnswers, uploadSupportingDocument } from "./usePreviewFileActions";

type Uploaded = { file: File; path: string };

export const usePreviewPrefill = (token: string | undefined): Prefill => {
    const latest = useRef(token);
    latest.current = token;
    const [uploaded, setUploaded] = useState<Uploaded[]>([]);

    const add = useCallback(async (files: File[]) => {
        const paths = await Promise.all(files.map((file) => uploadSupportingDocument(latest.current, file)));
        const added = files.flatMap((file, i) => {
            const path = paths[i];
            return path ? [{ file, path }] : [];
        });
        try {
            return await suggestAnswers(
                latest.current,
                added.map((u) => u.path)
            );
        } finally {
            setUploaded((current) => [...current, ...added]);
        }
    }, []);

    const documents = useMemo(
        () =>
            uploaded.map(({ file, path }) => ({
                key: path,
                name: file.name,
                size: file.size,
                open: () => showFile(file, file.name),
                remove: async () => {
                    setUploaded((current) => current.filter((u) => u.path !== path));
                    await deleteSupportingDocument(latest.current, path);
                },
            })),
        [uploaded]
    );

    return useMemo(() => ({ documents, add }), [documents, add]);
};
